import React, { useEffect, useState, useRef } from "react";

const GrnRecords = () => {
  const [vendors, setVendors] = useState([]);
  const [poCodes, setPoCodes] = useState([]);
  const [poList, setPoList] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [searchPo, setSearchPo] = useState("");
  const [searchInvoice, setSearchInvoice] = useState("");
  const [selectedPoCode, setSelectedPoCode] = useState("");
  const [searchSkuCode, setSearchSkuCode] = useState("");
  const [searchSkuName, setSearchSkuName] = useState("");
  const [highlightedPo, setHighlightedPo] = useState(null);
  const [filteredPoList, setFilteredPoList] = useState([]);
  const [skuMatches, setSkuMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const skuSearchTimer = useRef(null);
  const [expandedPo, setExpandedPo] = useState(null);
  const [poDetails, setPoDetails] = useState({});
  const [loadingPoList, setLoadingPoList] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState({});
  const [selectedPoType, setSelectedPoType] = useState("");
  const [grnChains, setGrnChains] = useState({});
  const [expandedPending, setExpandedPending] = useState(false);
  const [expandedInvoice, setExpandedInvoice] = useState({
    poCode: null,
    invoiceId: null,
  });
  const [isDark, setIsDark] = useState(
    document.documentElement.getAttribute("data-mui-color-scheme") === "dark"
  );

  const poRefs = useRef({});
  const poRowRefs = useRef({});
  const poDetailsCache = useRef({});

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const mode =
        document.documentElement.getAttribute("data-mui-color-scheme") === "dark";
      setIsDark(mode);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-mui-color-scheme"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    loadVendors();
    loadPoCodes();
    loadAllPoList();
  }, []);

  useEffect(() => {
    filterPOs();
  }, [
    selectedVendor,
    selectedMonth,
    searchPo,
    searchInvoice,
    selectedPoCode,
    selectedPoType,
    searchSkuCode,
    searchSkuName,
    poDetails,
  ]);

  // useEffect(() => {
  //   console.log(
  //     "PO List:",
  //     poList.map((po) => ({
  //       poCode: po.poCode,
  //       poType: po.poType,
  //       poCreatedDate: po.poCreatedDate,
  //       companyName: po.companyName,
  //       vendorCode: po.vendorCode,
  //       grnReceivedDate: po.grnReceivedDate,
  //       grnUpdatedAt: po.grnUpdatedAt,
  //       closed: po.closed,
  //       closedOnDate: po.closedOnDate,
  //     })),
  //   );
  // }, [poList]);

  useEffect(() => {
    const handleSkuSearch = async () => {
      if (!searchSkuCode && !searchSkuName) return;

      for (const po of poList) {
        const res = await fetch(
          `${
            process.env.REACT_APP_API_URL
          }/api?type=get-po-details&poCode=${encodeURIComponent(po.poCode)}`,
        );
        const details = await res.json();
        const items = Array.isArray(details)
          ? details
          : details?.invoices?.flatMap((inv) => inv.items) || [];

        const match = items.some(
          (item) =>
            (searchSkuCode &&
              item.skuCode
                ?.toLowerCase()
                .includes(searchSkuCode.toLowerCase())) ||
            (searchSkuName &&
              item.skuName
                ?.toLowerCase()
                .includes(searchSkuName.toLowerCase())),
        );
        if (match) {
          await viewPoDetails(po.poCode, true);
          break;
        }
      }
    };

    handleSkuSearch();
  }, [searchSkuCode, searchSkuName]);

  useEffect(() => {
    if (!searchSkuCode && !searchSkuName) {
      setHighlightedPo(null);
      setSkuMatches([]);
      setCurrentMatchIndex(-1);
    }
  }, [searchSkuCode, searchSkuName]);

  useEffect(() => {
    const preloadAllDetails = async () => {
      try {
        const promises = poList.map(async (po) => {
          const url = `${
            process.env.REACT_APP_API_URL
          }/api?type=get-po-details&poCode=${encodeURIComponent(po.poCode)}`;
          const res = await fetch(url);
          if (res.ok) {
            const data = await res.json();
            poDetailsCache.current[po.poCode] = data;
          }
        });

        await Promise.allSettled(promises);
        filterPOs();

        // console.log("✅ All PO details preloaded for instant SKU search");
      } catch (e) {
        console.error("Preload error:", e);
      }
    };

    if (poList.length > 0) preloadAllDetails();
  }, [poList]);

  const loadVendors = async () => {
    try {
      setLoadingPoList(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api?type=vendors`,
      );
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setVendors(Array.isArray(data) ? data : []);
      // console.log("Vendors:", data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setVendors([]);
    } finally {
      setLoadingPoList(false);
    }
  };

  const loadPoCodes = async () => {
    try {
      setLoadingPoList(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api?type=po-codes`,
      );
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setPoCodes(Array.isArray(data) ? data : []);
      // console.log("PO Codes:", data);
    } catch (error) {
      console.error("Error fetching PO codes:", error);
      setPoCodes([]);
    } finally {
      setLoadingPoList(false);
    }
  };

  const loadAllPoList = async () => {
    try {
      setLoadingPoList(true);
      const url = `${process.env.REACT_APP_API_URL}/api?type=po-list`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      // console.log("API Response for PO List:", data);
      setPoList(Array.isArray(data) ? data : []);
      setFilteredPoList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching PO list:", error);
      setPoList([]);
      setFilteredPoList([]);
    } finally {
      setLoadingPoList(false);
    }
  };

  const filterPOs = () => {
    if (!poList || poList.length === 0) {
      setFilteredPoList([]);
      return;
    }

    let filtered = [...poList];

    // Vendor Filter
    if (selectedVendor) {
      filtered = filtered.filter(
        (po) => po.vendorCode?.toLowerCase() === selectedVendor.toLowerCase(),
      );
    }

    // Month Filter (yyyy-mm)
    if (selectedMonth) {
      const [year, month] = selectedMonth.split("-");
      filtered = filtered.filter((po) => {
        if (!po.poCreatedDate) return false;
        const date = new Date(po.poCreatedDate);
        return (
          date.getFullYear().toString() === year &&
          (date.getMonth() + 1).toString().padStart(2, "0") === month
        );
      });
    }

    // PO Code Dropdown
    if (selectedPoCode) {
      filtered = filtered.filter(
        (po) => po.poCode?.toLowerCase() === selectedPoCode.toLowerCase(),
      );
    }

    // PO Type Filter
    if (selectedPoType) {
      filtered = filtered.filter((po) => po.poType === selectedPoType);
    }

    // Invoice Number Filter
    if (searchInvoice) {
      const query = searchInvoice.toLowerCase();

      filtered = filtered.filter((po) => {
        const cached = poDetailsCache.current[po.poCode];

        if (!cached) {
          return false;
        }

        if (po.poType === "LSL") {
          return (cached.invoices || []).some((inv) =>
            normalize(inv.invoiceNumber).includes(normalize(query)),
          );
        }

        const invoiceNo =
          cached?.invoiceNumber || cached?.invoices?.[0]?.invoiceNumber || "";

        return normalize(invoiceNo).includes(normalize(query));
      });
    }

    // Search PO Number
    if (searchPo) {
      const query = searchPo.toLowerCase();
      filtered = filtered.filter(
        (po) =>
          po.poCode?.toLowerCase().includes(query) ||
          po.companyName?.toLowerCase().includes(query) ||
          po.vendorCode?.toLowerCase().includes(query),
      );
    }

    // SKU Code / SKU Name Filters — use cached details only
    if (searchSkuCode || searchSkuName) {
      const code = searchSkuCode.toLowerCase();
      const name = searchSkuName.toLowerCase();

      const matchedPOs = filtered.filter((po) => {
        const details =
          poDetailsCache.current[po.poCode] || poDetails[po.poCode];

        if (!details) return false;

        // normalize items (Fresh + LSL)
        const items = [
          ...(details?.invoices?.flatMap((inv) => inv.items) || []),
          ...(details?.pendingItems || []),
          ...(details?.rows || []),
        ];

        if (items.length === 0) return false;

        return items.some(
          (item) =>
            (code && normalize(item.skuCode).includes(normalize(code))) ||
            (name && normalize(item.skuName).includes(normalize(name))),
        );
      });

      filtered = matchedPOs;
    }

    setFilteredPoList(filtered);
  };

  const viewPoDetails = async (
    poCode,
    shouldScroll = false,
    permanentHighlight = false,
  ) => {
    if (!poCode) {
      console.error("No poCode provided for viewPoDetails");
      return;
    }

    if (expandedPo === poCode) {
      setExpandedPo(null);
      setExpandedInvoice(null);
      return;
    }

    // Scroll to the PO summary row immediately
    const summaryEl = poRowRefs.current[poCode];
    if (shouldScroll && summaryEl) {
      summaryEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    // Find PO to determine type (Fresh / LSL)
    const po = poList.find((p) => p.poCode === poCode);
    const poType = po?.poType;

    /* ===================== USE CACHE FIRST ===================== */
    if (poDetailsCache.current[poCode]) {
      const cached = poDetailsCache.current[poCode];

      if (poType === "LSL") {
        const invoices = cached.invoices || [];
        const pendingItems = cached.pendingItems || [];

        poDetailsCache.current[poCode] = {
          invoices,
          pendingItems,
        };

        setGrnChains((prev) => ({
          ...prev,
          [poCode]: invoices,
        }));

        setExpandedPo(poCode);
        setExpandedInvoice(null);

        if (permanentHighlight) setHighlightedPo(poCode);

        if (shouldScroll) {
          setTimeout(() => {
            const detailEl = poRefs.current[poCode];
            if (detailEl)
              detailEl.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }

        return;
      } else {
        const rows =
          cached?.rows ||
          (Array.isArray(cached)
            ? cached
            : cached?.invoices?.[0]?.items || cached?.items || []);

        setPoDetails((prev) => ({ ...prev, [poCode]: rows }));
        setExpandedPo(poCode);

        if (permanentHighlight) setHighlightedPo(poCode);

        if (shouldScroll) {
          setTimeout(() => {
            const detailEl = poRefs.current[poCode];
            if (detailEl)
              detailEl.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }

        return;
      }
    }

    /* ===================== FETCH FROM API ===================== */
    setLoadingDetails((prev) => ({ ...prev, [poCode]: true }));
    setPoDetails((prev) => ({ ...prev, [poCode]: [] }));

    try {
      const url = `${
        process.env.REACT_APP_API_URL
      }/api?type=get-po-details&poCode=${encodeURIComponent(poCode)}`;
      // console.log(`Fetching PO details: ${url}`);

      const res = await fetch(url);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(
          `HTTP error! status: ${res.status}, body: ${errorText}`,
        );
      }

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const errorText = await res.text();
        throw new Error(`Expected JSON, got ${contentType}: ${errorText}`);
      }

      const raw = await res.json();
      const normalized = raw;

      poDetailsCache.current[poCode] = normalized;

      if (poType === "LSL") {
        const invoices = normalized?.invoices || [];
        const pendingItems = normalized?.pendingItems || [];

        poDetailsCache.current[poCode] = {
          ...normalized,
          invoices,
          pendingItems,
        };

        setGrnChains((prev) => ({
          ...prev,
          [poCode]: invoices,
        }));

        setExpandedPo(poCode);
        setExpandedInvoice(null);
      } else {
        // Fresh / Normal PO
        const rows =
          normalized?.rows ||
          (Array.isArray(normalized)
            ? normalized
            : normalized?.invoices?.[0]?.items || normalized?.items || []);

        setPoDetails((prev) => ({ ...prev, [poCode]: rows }));
        setExpandedPo(poCode);
      }

      if (permanentHighlight) {
        setHighlightedPo(poCode);
      }

      if (shouldScroll) {
        setTimeout(() => {
          const detailEl = poRefs.current[poCode];
          if (detailEl)
            detailEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    } catch (error) {
      console.error(`Error fetching PO details for ${poCode}:`, error);
      setPoDetails((prev) => ({ ...prev, [poCode]: [] }));
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [poCode]: false }));
    }
  };

  const findSkuMatchesAcrossPos = async (skuCode, skuName) => {
    const code = skuCode?.trim().toLowerCase() || "";
    const name = skuName?.trim().toLowerCase() || "";

    if (!code && !name) {
      setSkuMatches([]);
      setCurrentMatchIndex(-1);
      return [];
    }

    const matches = [];

    for (const po of poList) {
      const poCode = po.poCode;

      const details = poDetailsCache.current[poCode] || poDetails[poCode] || [];

      if (!details) continue;

      const items = Array.isArray(details)
        ? details
        : details?.invoices?.flatMap((inv) => inv.items) || [];

      if (items.length === 0) continue;

      const found = items.some(
        (item) =>
          (code && item.skuCode?.toLowerCase().includes(code)) ||
          (name && item.skuName?.toLowerCase().includes(name)),
      );

      if (found) matches.push(poCode);
    }

    setSkuMatches(matches);
    setCurrentMatchIndex(matches.length ? 0 : -1);

    // console.log(
    //   `🔍 Found ${matches.length} PO(s) matching SKU filter`,
    //   matches,
    // );

    return matches;
  };

  const goToMatchAtIndex = async (index) => {
    if (!skuMatches || skuMatches.length === 0) return;
    const safeIndex =
      ((index % skuMatches.length) + skuMatches.length) % skuMatches.length;
    const targetPo = skuMatches[safeIndex];
    setCurrentMatchIndex(safeIndex);

    setHighlightedPo(null);

    await viewPoDetails(targetPo, true, true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "") return "-";
    const d = new Date(dateStr);
    return isNaN(d) ? "-" : d.toLocaleDateString();
  };

  const formatNumber = (value) => {
    return value === null || value === undefined ? "0" : value.toString();
  };

  const renderMrpIndicator = (ordered, received) => {
    const o = Number(ordered);
    const r = Number(received);

    if (isNaN(o) || isNaN(r)) {
      return {
        style: {},
        indicator: null,
      };
    }

    if (r > o) {
      return {
        style: {
          backgroundColor: " #166534",
          color: "#dcfce7",
          fontWeight: "600",
        },
        indicator: <span style={{ marginLeft: "6px" }}>⬆️</span>,
      };
    }

    if (r < o && r > null) {
      return {
        style: {
          backgroundColor: "#ac3636",
          color: "#fee2e2",
          fontWeight: "600",
        },
        indicator: <span style={{ marginLeft: "6px" }}>⬇️</span>,
      };
    }

    return {
      style: {},
      indicator: null,
    };
  };

  const getTotalReceivedQty = (poCode) => {
    const cached = poDetailsCache.current[poCode];

    if (!cached) return 0;

    // LSL PO
    if (cached.invoices && cached.invoices.length > 0) {
      return cached.invoices.reduce((total, inv) => {
        const invTotal = (inv.items || []).reduce(
          (sum, item) => sum + Number(item.receivedQty || 0),
          0,
        );
        return total + invTotal;
      }, 0);
    }

    // Pending GRN Items
    if (cached.pendingItems && cached.pendingItems.length > 0) {
      return cached.pendingItems.reduce(
        (sum, item) => sum + Number(item.receivedQty || 0),
        0,
      );
    }

    // Fresh PO
    const rows = cached?.rows || [];
    return rows.reduce((sum, item) => sum + Number(item.receivedQty || 0), 0);
  };

  const normalize = (val) => (val || "").toString().trim().toLowerCase();

  return (
    <div
      style={{
        padding: "10px 30px",
        background: isDark ? "#0f172a" : "#ECF0F3",
        color: isDark ? "#e5e7eb" : "#111827",
        minHeight: "100vh",
      }}
    >
      <div style={{ marginBottom: "25px" }}>
        <h2
          style={{
            fontSize: "26px",
            fontWeight: "700",
            color: isDark ? "#f9fafb" : "#424141",
          }}
        >
          📦 GRN Records
        </h2>

        <p style={{ color: isDark ? "#9ca3af" : "#6b7280" , marginTop: "4px" }}>
          Track all GRN records, invoices and PO deliveries
        </p>
      </div>

      {/* FILTER CARD */}
      <div
        style={{
          background: isDark ? "#1e293b" : "#ffffff",
          color: isDark ? "#e5e7eb" : "#111827",
          padding: "15px 20px",
          borderRadius: "14px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          marginBottom: "25px",
        }}
      >
        {/* FILTER ROW 1 */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "20px",
            marginBottom: "15px",
            alignItems: "center",
          }}
        >
          {/* Vendor */}
          <select
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
            className="reminder-input"
          >
            <option value="">Choose Vendor</option>
            {vendors.map((v) => (
              <option key={v.vendorCode} value={v.vendorCode}>
                {v.companyName
                  ? `${v.companyName} (${v.vendorCode})`
                  : `Unknown (${v.vendorCode})`}
              </option>
            ))}
          </select>

          {/* PO Code */}
          <select
            value={selectedPoCode}
            onChange={(e) => setSelectedPoCode(e.target.value)}
            className="reminder-input"
          >
            <option value="">Select PO Code</option>
            {poCodes.map((p) => (
              <option key={p.poCode} value={p.poCode}>
                {p.poCode}
              </option>
            ))}
          </select>

          {/* PO Type */}
          <select
            value={selectedPoType}
            onChange={(e) => setSelectedPoType(e.target.value)}
            className="reminder-input"
          >
            <option value="">PO Type</option>
            <option value="Fresh">Fresh</option>
            <option value="LSL">Low Shelf Life</option>
          </select>

          {/* Month */}
          <div>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="reminder-input"
            />
          </div>
        </div>

        {/* FILTER ROW 2 */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "20px",
          }}
        >
          <input
            type="text"
            placeholder="🔍 Search PO Number"
            value={searchPo}
            onChange={(e) => setSearchPo(e.target.value)}
            className="reminder-input"
          />

          <input
            type="text"
            placeholder="🔍 Search Invoice Number"
            value={searchInvoice}
            onChange={(e) => setSearchInvoice(e.target.value)}
            className="reminder-input"
          />

          <input
            type="text"
            placeholder="🔍 Search SKU Code"
            value={searchSkuCode}
            onChange={(e) => {
              const val = e.target.value;
              setSearchSkuCode(val);

              if (skuSearchTimer.current) clearTimeout(skuSearchTimer.current);

              skuSearchTimer.current = setTimeout(() => {
                findSkuMatchesAcrossPos(val, searchSkuName);
              }, 250);
            }}
            onKeyDown={async (e) => {
              if (e.key === "Enter") {
                if (skuMatches.length === 0) {
                  await findSkuMatchesAcrossPos(searchSkuCode, searchSkuName);
                  if (skuMatches.length > 0) await goToMatchAtIndex(0);
                } else {
                  await goToMatchAtIndex(currentMatchIndex + 1);
                }
              }
            }}
            className="reminder-input"
          />

          <input
            type="text"
            placeholder="🔍 Search SKU Name"
            value={searchSkuName}
            onChange={(e) => {
              const val = e.target.value;
              setSearchSkuName(val);

              if (skuSearchTimer.current) clearTimeout(skuSearchTimer.current);

              skuSearchTimer.current = setTimeout(() => {
                findSkuMatchesAcrossPos(searchSkuCode, val);
              }, 250);
            }}
            onKeyDown={async (e) => {
              if (e.key === "Enter") {
                if (skuMatches.length === 0) {
                  await findSkuMatchesAcrossPos(searchSkuCode, searchSkuName);
                  if (skuMatches.length > 0) await goToMatchAtIndex(0);
                } else {
                  await goToMatchAtIndex(currentMatchIndex + 1);
                }
              }
            }}
            className="reminder-input"
          />
        </div>
      </div>

      <div
        style={{
          background: isDark ? "#1e293b" : "#ffffff",
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }}
      >
        <div
          style={{
            overflowX: "auto",
            background: isDark ? "#1e293b" : "#ffffff",
            border: isDark ? "1px solid #334155" : "1px solid #e5e7eb",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
            }}
          >
            <thead style={{ backgroundColor: isDark ? "#4f9f92" : "#75bfb0", }}>
              <tr>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}
                >
                  PO Code
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}
                >
                  PO Type
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}
                >
                  PO Date
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}
                >
                  Vendor Name
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}
                >
                  Invoice Received On
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}
                >
                  GRN Updated At
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}
                >
                  View Details
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}
                >
                  PO Status
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingPoList ? (
                <tr>
                  <td
                    colSpan="8"
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      color: "#6b7280",
                    }}
                  >
                    <div className="loader"></div>
                  </td>
                </tr>
              ) : filteredPoList.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      color: "#6b7280",
                    }}
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                filteredPoList.map((po, index) => (
                  <React.Fragment key={po.poCode}>
                    <tr
                      style={{
                        backgroundColor: isDark
                          ? index % 2 === 0
                            ? "#1e293b"
                            : "#0f172a"
                          : index % 2 === 0
                          ? "#fff"
                          : "#f9fafb",
                        color: isDark ? "#e5e7eb" : "#111827",
                        transition: "background-color 0.2s",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.backgroundColor = isDark ? "#334155" : "#dbeafe")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.backgroundColor = isDark
                          ? index % 2 === 0
                            ? "#1e293b"
                            : "#0f172a"
                          : index % 2 === 0
                          ? "#fff"
                          : "#f9fafb")
                      }
                    >
                      <td style={{ padding: "12px" }}>{po.poCode}</td>
                      <td style={{ padding: "12px" }}>{po.poType || "-"}</td>
                      <td style={{ padding: "12px" }}>
                        {formatDate(po.poCreatedDate)}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {po.companyName
                          ? `${po.companyName} (${po.vendorCode || ""})`
                          : "Unknown Vendor"}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {formatDate(po.grnReceivedDate)}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {formatDate(po.grnUpdatedAt)}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button
                          onClick={() => viewPoDetails(po.poCode, false, false)}
                          style={{
                            backgroundColor: "#75BFB0",
                            color: "#fff",
                            padding: "4px 16px",
                            borderRadius: "8px",
                            transition: "background-color 0.2s",
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.backgroundColor = "#424141")
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.backgroundColor = "#75BFB0")
                          }
                        >
                          {expandedPo === po.poCode ? "Hide" : "View"}
                        </button>
                      </td>
                      <td style={{ padding: "12px" }}>
                        {po.closed === 1 ? (
                          <span style={{ color: "red", fontWeight: "bold" }}>
                            Closed
                          </span>
                        ) : (
                          <span style={{ color: isDark ? "fff" : "green", fontWeight: "bold" }}>
                            Open until: {formatDate(po.closedOnDate)}
                          </span>
                        )}
                      </td>
                    </tr>

                    {expandedPo === po.poCode && (
                      <tr ref={(el) => (poRefs.current[po.poCode] = el)}>
                        <td
                          colSpan="8"
                          style={{
                            backgroundColor:
                              highlightedPo === po.poCode
                                ? "#ffeb99"
                                : isDark ? "#1e293b" : "#f2f3f5",
                                
                            transition: "background-color 0.5s ease",
                            padding: "16px",
                            paddingTop: "0px",
                          }}
                        >
                          {(() => {
                            const pendingItems =
                              poDetailsCache.current[po.poCode]?.pendingItems ||
                              [];

                            const invoices = Array.isArray(grnChains[po.poCode])
                              ? grnChains[po.poCode]
                              : [];

                            return (
                              <>
                                <h3
                                  style={{
                                    fontWeight: "600",
                                    fontSize: "1.125rem",
                                    marginBottom: "12px",
                                    color: isDark ? "#ffffff" : "#1e293b",
                                  }}
                                >
                                  PO Details - {po.poCode} | PO Type -{" "}
                                  {po.poType}
                                  {po.poType !== "LSL" &&
                                    (() => {
                                      const cached =
                                        poDetailsCache.current[po.poCode];
                                      const invoiceNo =
                                        cached?.invoiceNumber ||
                                        cached?.invoices?.[0]?.invoiceNumber;

                                      return invoiceNo
                                        ? ` | Invoice No - ${invoiceNo}`
                                        : "";
                                    })()}
                                  {" | Total Received Qty - "}
                                  {getTotalReceivedQty(po.poCode)}
                                </h3>

                                {/* ===================== LSL PO VIEW ===================== */}
                                {po.poType === "LSL" ? (
                                  <div style={{ marginBottom: "16px" }}>
                                    {/* 🔹 INVOICE CHAINS */}
                                    {invoices.map((inv, index) => (
                                      <div
                                        key={inv.grnId}
                                        style={{
                                          marginBottom: "18px",
                                          border: "1px solid #e5e7eb",
                                          borderRadius: "8px",
                                          background: isDark ? "#1e293b" : "#ffffff",
                                          boxShadow:
                                            "0 1px 4px rgba(0,0,0,0.08)",
                                        }}
                                      >
                                        {/* Invoice Header */}
                                        <div
                                          style={{
                                            padding: "10px 14px",
                                            background: "#75BFB0",
                                            color: "#fff",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            borderRadius: "8px 8px 0 0",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            {index + 1}. Invoice No:{" "}
                                            {inv.invoiceNumber}
                                          </span>

                                          <button
                                            onClick={() =>
                                              setExpandedInvoice(
                                                expandedInvoice === inv.grnId
                                                  ? null
                                                  : inv.grnId,
                                              )
                                            }
                                            style={{
                                              backgroundColor: isDark ? "#1e293b" : "#fff",
                                              color: isDark ? "#fff" : "#1e293b",
                                              padding: "6px 16px",
                                              borderRadius: "6px",
                                              fontSize: "0.8rem",
                                              fontWeight: "600",
                                              border: "none",
                                              cursor: "pointer",
                                              maxWidth: "110px",
                                              height: "30px",
                                            }}
                                          >
                                            {expandedInvoice === inv.grnId
                                              ? "Hide Details"
                                              : "View Details"}
                                          </button>
                                        </div>

                                        {/* Invoice Items */}
                                        {expandedInvoice === inv.grnId && (
                                          <div
                                            style={{
                                              overflowX: "auto",
                                              padding: "10px",
                                            }}
                                          >
                                            <table
                                              style={{
                                                width: "100%",
                                                borderCollapse: "collapse",
                                              }}
                                            >
                                              <thead
                                                style={{
                                                  backgroundColor: "#75BFB0",
                                                  color: "#fff",
                                                }}
                                              >
                                                <tr>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "left",
                                                    }}
                                                  >
                                                    SKU Code
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "left",
                                                    }}
                                                  >
                                                    SKU Name
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Ordered MRP
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Expected Qty
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Expected Shelf Life %
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Before Qty
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Received Qty
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    After Qty
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Received MRP
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Received Shelf Life %
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Damaged
                                                  </th>
                                                  <th
                                                    style={{
                                                      padding: "10px",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    Expiry Date
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {inv.items.map((d, i) => (
                                                  <tr
                                                    key={i}
                                                    style={{
                                                      backgroundColor: isDark
                                                        ? i % 2 === 0
                                                          ? "#1e293b"
                                                          : "#0f172a"
                                                        : i % 2 === 0
                                                        ? "#ffffff"
                                                        : "#f8f9fa",
                                                      color: isDark ? "#e5e7eb" : "#111827",
                                                    }}
                                                  >
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                      }}
                                                    >
                                                      {d.skuCode}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                      }}
                                                    >
                                                      {d.skuName}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      ₹{d.mrp}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      {d.expectedQty}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      {d.shelfLifePercent !=
                                                      null
                                                        ? (() => {
                                                            const v = Number(
                                                              d.shelfLifePercent,
                                                            );
                                                            return `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
                                                          })()
                                                        : "-"}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      {d.beforeQty || "NA"}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      {d.receivedQty}
                                                    </td>
                                                     <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      {d.afterQty || "NA"}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                        ...renderMrpIndicator(
                                                          d.mrp,
                                                          d.receivedMrp,
                                                        ).style,
                                                      }}
                                                    >
                                                      ₹{d.receivedMrp}
                                                      {
                                                        renderMrpIndicator(
                                                          d.mrp,
                                                          d.receivedMrp,
                                                        ).indicator
                                                      }
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      {d.receivedShelfLifePercent !=
                                                      null
                                                        ? (() => {
                                                            const v = Number(
                                                              d.receivedShelfLifePercent,
                                                            );
                                                            return `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
                                                          })()
                                                        : "-"}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      {d.damaged}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      {formatDate(d.expiryDate)}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                      </div>
                                    ))}

                                    {/* 🔹 PENDING GRN SECTION */}
                                    {pendingItems.length > 0 && (
                                      <div
                                        style={{
                                          marginTop: "20px",
                                          border: "1px dashed #f59e0b",
                                          borderRadius: "8px",
                                          background: "#fff7ed",
                                        }}
                                      >
                                        <div
                                          style={{
                                            padding: "10px 14px",
                                            background: "rgb(252, 149, 5)",
                                            color: "#fff",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            borderRadius: "8px 8px 0 0",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            {pendingItems.every(
                                              (item) =>
                                                (item.pendingQty ?? 0) === 0,
                                            )
                                              ? `Fully Received Items (${pendingItems.length})`
                                              : `Pending GRN Items (${pendingItems.filter((item) => (item.pendingQty ?? 0) > 0).length})`}
                                          </span>

                                          <button
                                            onClick={() =>
                                              setExpandedPending(
                                                !expandedPending,
                                              )
                                            }
                                            style={{
                                              backgroundColor: isDark ? "#1e293b" : "#fff",
                                              color: isDark ? "#fff" : "#1e293b",
                                              padding: "6px 16px",
                                              borderRadius: "6px",
                                              fontSize: "0.8rem",
                                              fontWeight: "600",
                                              border: "none",
                                              cursor: "pointer",
                                              maxWidth: "110px",
                                              height: "30px",
                                            }}
                                          >
                                            {expandedPending
                                              ? "Hide Details"
                                              : "View Details"}
                                          </button>
                                        </div>

                                        {expandedPending && (
                                          <div
                                            style={{
                                              overflowX: "auto",
                                              padding: "10px",
                                              background: isDark ? "#1e293b" : "#fff",
                                            }}
                                          >
                                            {pendingItems.length === 0 ? (
                                              <div
                                                style={{
                                                  textAlign: "center",
                                                  padding: "16px",
                                                  color: "#666",
                                                }}
                                              >
                                                No pending items
                                              </div>
                                            ) : (
                                              <table
                                                style={{
                                                  width: "100%",
                                                  borderCollapse: "collapse",
                                                  background: "#fff",
                                                }}
                                              >
                                                <thead
                                                  style={{
                                                    backgroundColor: "#75BFB0",
                                                    color: "#fff",
                                                  }}
                                                >
                                                  <tr>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "left",
                                                      }}
                                                    >
                                                      SKU Code
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "left",
                                                      }}
                                                    >
                                                      SKU Name
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Expected Qty
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Ordered MRP
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Expected Shelf Life %
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Before Qty
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Received Qty
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      After Qty
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Received MRP
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Received Shelf Life %
                                                    </th>
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Damaged
                                                    </th>

                                                    {pendingItems.some(
                                                      (item) =>
                                                        (item.pendingQty ?? 0) >
                                                        0,
                                                    ) && (
                                                      <th
                                                        style={{
                                                          padding: "10px",
                                                          textAlign: "center",
                                                          fontWeight: "bold",
                                                          color: "#fff",
                                                          backgroundColor:
                                                            "rgb(252, 149, 5)",
                                                        }}
                                                      >
                                                        Still Pending
                                                      </th>
                                                    )}
                                                    <th
                                                      style={{
                                                        padding: "10px",
                                                        textAlign: "center",
                                                      }}
                                                    >
                                                      Expiry Date
                                                    </th>
                                                  </tr>
                                                </thead>

                                                <tbody>
                                                  {pendingItems.map((d, i) => {
                                                    const received = Number(
                                                      d.receivedQty ?? 0,
                                                    );
                                                    const receivedShelfLife =
                                                      Number(
                                                        d.receivedShelfLifePercent ??
                                                          0,
                                                      );
                                                    const damaged = Number(
                                                      d.damaged ?? 0,
                                                    );
                                                    const pending = Number(
                                                      d.pendingQty ?? 0,
                                                    );

                                                    return (
                                                      <tr
                                                        key={i}
                                                        style={{
                                                          backgroundColor: isDark
                                                            ? i % 2 === 0
                                                              ? "#1e293b"
                                                              : "#0f172a"
                                                            : i % 2 === 0
                                                            ? "#ffffff"
                                                            : "#fffbeb",
                                                          color: isDark ? "#e5e7eb" : "#111827",
                                                        }}
                                                      >
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                          }}
                                                        >
                                                          {d.skuCode}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                          }}
                                                        >
                                                          {d.skuName}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          {d.expectedQty}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          ₹{d.mrp}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          {d.shelfLifePercent !=
                                                          null
                                                            ? (() => {
                                                                const v =
                                                                  Number(
                                                                    d.shelfLifePercent,
                                                                  );
                                                                return `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
                                                              })()
                                                            : "-"}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          {d.beforeQty || "NA"}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          {received}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          {d.afterQty || "NA"}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                            ...renderMrpIndicator(
                                                              d.mrp,
                                                              d.receivedMrp,
                                                            ).style,
                                                          }}
                                                        >
                                                          ₹{d.receivedMrp}
                                                          {
                                                            renderMrpIndicator(
                                                              d.mrp,
                                                              d.receivedMrp,
                                                            ).indicator
                                                          }
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          {receivedShelfLife}
                                                        </td>
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          {damaged}
                                                        </td>
                                                        {pendingItems.some(
                                                          (item) =>
                                                            (item.pendingQty ??
                                                              0) > 0,
                                                        ) && (
                                                          <td
                                                            style={{
                                                              padding: "10px",
                                                              textAlign:
                                                                "center",
                                                              fontWeight:
                                                                "bold",
                                                              color:
                                                                pending > 0
                                                                  ? "#d97706"
                                                                  : "#16a34a",
                                                            }}
                                                          >
                                                            {pending}
                                                          </td>
                                                        )}
                                                        <td
                                                          style={{
                                                            padding: "10px",
                                                            textAlign: "center",
                                                          }}
                                                        >
                                                          {formatDate(
                                                            d.expiryDate,
                                                          )}
                                                        </td>
                                                      </tr>
                                                    );
                                                  })}
                                                </tbody>
                                              </table>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  /* ===================== NORMAL / FRESH PO VIEW ===================== */
                                  <div
                                    style={{
                                      overflowX: "auto",
                                      border: "1px solid #e5e7eb",
                                      borderRadius: "8px",
                                      background: isDark ? "#1e293b" : "#ffffff",
                                      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                                    }}
                                  >
                                    <table
                                      style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                      }}
                                    >
                                      <thead
                                        style={{
                                          backgroundColor: "#75BFB0",
                                          color: "#fff",
                                        }}
                                      >
                                        <tr>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "left",
                                            }}
                                          >
                                            SKU Code
                                          </th>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "left",
                                            }}
                                          >
                                            SKU Name
                                          </th>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Expected Qty
                                          </th>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Ordered MRP
                                          </th>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Before Qty
                                          </th>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Received Qty
                                          </th>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "center",
                                            }}
                                          >
                                            After Qty
                                          </th>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Received MRP
                                          </th>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Received Shelf Life %
                                          </th>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Damaged
                                          </th>
                                          <th
                                            style={{
                                              padding: "10px",
                                              textAlign: "center",
                                            }}
                                          >
                                            Expiry Date
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(poDetails[po.poCode] || []).map(
                                          (d, i) => (
                                            <tr
                                              key={i}
                                              style={{
                                                backgroundColor: isDark
                                                  ? i % 2 === 0
                                                    ? "#1e293b"
                                                    : "#0f172a"
                                                  : i % 2 === 0
                                                  ? "#ffffff"
                                                  : "#f8f9fa",
                                                color: isDark ? "#e5e7eb" : "#111827",
                                              }}
                                            >
                                              <td style={{ padding: "10px" }}>
                                                {d.skuCode}
                                              </td>
                                              <td style={{ padding: "10px" }}>
                                                {d.skuName}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "10px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {d.expectedQty}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "10px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                ₹{d.mrp}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "10px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {d.beforeQty || 'NA'}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "10px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {d.receivedQty ?? 0}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "10px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {d.afterQty || "NA"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "10px",
                                                  textAlign: "center",
                                                  ...renderMrpIndicator(
                                                    d.mrp,
                                                    d.receivedMrp,
                                                  ).style,
                                                }}
                                              >
                                                ₹{d.receivedMrp}
                                                {
                                                  renderMrpIndicator(
                                                    d.mrp,
                                                    d.receivedMrp,
                                                  ).indicator
                                                }
                                              </td>
                                              <td
                                                style={{
                                                  padding: "10px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {d.receivedShelfLifePercent !=
                                                null
                                                  ? (() => {
                                                      const v = Number(
                                                        d.receivedShelfLifePercent,
                                                      );
                                                      return `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
                                                    })()
                                                  : "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "10px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {d.damaged ?? 0}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "10px",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {formatDate(d.expiryDate)}
                                              </td>
                                            </tr>
                                          ),
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <style>{`
        .reminder-input{
          padding:11px 12px;
          border-radius:8px;
          border:1px solid #d1d5db;
          font-size:14px;
          outline:none;
          height:40px;
          width:100%;
          box-sizing:border-box;
          background:white;
          color:black;
        }

        .reminder-input:focus{
          border-color:#75BFB0;
          box-shadow:0 0 0 2px rgba(117,191,176,0.2);
        }

        .th{
          padding:12px;
          text-align:left;
          font-weight:600;
        }

        .td{
          padding:12px;
        }

        .loader {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #75BFB0;
          border-radius: 50%;
          width: 35px;
          height: 35px;
          animation: spin 1s linear infinite;
          margin: auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* DARK MODE */
        [data-mui-color-scheme="dark"] .reminder-input {
          background: #0f172a;
          color: #e5e7eb;
          border: 1px solid #334155;
        }

        [data-mui-color-scheme="dark"] .reminder-input::placeholder {
          color: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default GrnRecords;
